import type { ElectricityConsumptionNarrativeInput } from './electricityConsumptionNarrative';

export interface ElectricityMethodologyContext {
  locationName: string;
  dataSourcesFromDb: string[];
  methodsFromDb: string[];
  usesEia861Pilot?: boolean;
  residentialExtrapModelLabel?: string | null;
  commercialExtrapModelLabel?: string | null;
  viewId: ElectricityConsumptionNarrativeInput['viewId'];
}

/**
 * User-facing methodology from traced pipeline:
 * elec_res_format / elec_com_format (fetchMethodologyData in Energy.tsx) + fetchElectricitySeries.
 */
export function buildElectricityPrimarySources(ctx: ElectricityMethodologyContext): string[] {
  if (ctx.usesEia861Pilot) {
    return [
      'U.S. Energy Information Administration Form EIA-861 — retail electricity sales by customer class; Burbank Water and Power utility ID 2507; native reporting geography: utility service territory.',
      'Published CAP activity values (comparison bars only; not the same as EIA nonresidential classification).',
    ];
  }
  const utilityLines =
    ctx.dataSourcesFromDb.length > 0
      ? ctx.dataSourcesFromDb.map(
          (u) =>
            `${u} — retail electricity sales by customer class (residential and commercial); reporting geography: utility service territory allocated to ${ctx.locationName}.`
        )
      : [
          `Serving electric utility or community choice aggregator retail sales for ${ctx.locationName} (customer-class sales by reporting year; see Technical details for database tables).`,
        ];

  return [
    ...utilityLines,
    'U.S. Census Bureau population estimates — annual place population for per-capita views (population table).',
  ];
}

export function buildElectricityClimatePlansCalculation(ctx: ElectricityMethodologyContext): string[] {
  const steps: string[] = [
    'Observed kWh years are taken from format-table rows where measurement = observed.',
    'Residential gap years (when enabled) use the selected residential extrapolation model (population scaling, county YoY, or average of those methods).',
    'Commercial gap years use guardrail-approved M1–M7 model predictions or the selected commercial extrapolation model.',
    'Customize table edits are treated as observed anchors; intermediate years may be linearly interpolated.',
    'Stacked total view sums residential and reported commercial customer-class sales for years where both sectors have values.',
  ];
  if (ctx.residentialExtrapModelLabel) {
    steps.push(`Active residential extrapolation: ${ctx.residentialExtrapModelLabel}.`);
  }
  if (ctx.commercialExtrapModelLabel) {
    steps.push(`Active commercial extrapolation/model: ${ctx.commercialExtrapModelLabel}.`);
  }
  if (ctx.viewId === 'perCapita') {
    steps.push(
      'Per-capita kWh divides residential plus reported commercial sales by place population for each year.',
      'California comparison uses the same customer-class combination and state population for the common chart year.'
    );
  }
  if (ctx.methodsFromDb.length) {
    steps.push(...ctx.methodsFromDb.filter((m) => !steps.some((s) => s.includes(m.slice(0, 20)))));
  }
  return steps;
}

export const ELECTRICITY_TECHNICAL_PROVENANCE = [
  'Supabase: elec_res_format, elec_com_format',
  'components/charts/EnergyUseSupabase.ts — fetchElectricitySeries',
  'components/pages/Energy.tsx — fetchMethodologyData, KPI + narrative builders',
  'domains/narrative/electricityConsumptionNarrative.ts',
];
